import { useEffect } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth-context";
import { createWalletProfile, PRODUCT_HOME } from "@/lib/product";
import { auth0Enabled, captureHostedAuthReturnTo } from "@/lib/auth0";
import { isAdminPathname, normalizeAppPathname } from "@/lib/route-path";
import { isWalletCloudMode } from "@/lib/runtime-mode";

const PUBLIC_PATHS = new Set([
  "/alliancedao",
  "/device",
  "/login",
  "/signup",
  "/password-reset",
  "/email-verification",
  "/auth/callback",
  "/privacy",
  "/terms-and-conditions",
  "/request-access",
]);
const SIGNED_IN_REDIRECT_PATHS = new Set(["/login", "/signup", "/password-reset", "/email-verification", "/auth/callback"]);
const ONBOARDING_PATH = "/onboarding";

function RouteCanvasFallback({ wallet }: { wallet?: boolean }) {
  return <div className={`${wallet ? "wallet-route-canvas" : "genrl-route-canvas"} min-h-screen w-full`} aria-hidden="true" />;
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, ready, completeOnboarding } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const routePathname = normalizeAppPathname(pathname);
  const adminRoute = isAdminPathname(routePathname);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ready || adminRoute) return;
    const isPublic = PUBLIC_PATHS.has(routePathname);
    const isOnboarding = routePathname === ONBOARDING_PATH;

    if (!user && !isPublic) {
      if (auth0Enabled() && typeof window !== "undefined") {
        captureHostedAuthReturnTo();
        window.location.replace(`${window.location.origin}/ui/login`);
        return;
      }
      navigate({ to: "/login" });
      return;
    }
    if (user && !user.daemon) {
      completeOnboarding(createWalletProfile(user));
      if (!isWalletCloudMode()) {
        navigate({ to: PRODUCT_HOME });
      }
      return;
    }
    if (user && (SIGNED_IN_REDIRECT_PATHS.has(routePathname) || isOnboarding)) {
      navigate({ to: PRODUCT_HOME });
    }
  }, [ready, user, routePathname, adminRoute, navigate, completeOnboarding]);

  const walletRoute = routePathname === "/early-access" || routePathname === "/wallet";

  if (adminRoute) return <>{children}</>;
  if (!ready) return <RouteCanvasFallback wallet={walletRoute} />;

  const isPublic = PUBLIC_PATHS.has(routePathname);

  // While the redirect is settling, don't render the protected shell
  if (!user && !isPublic) return <RouteCanvasFallback wallet={walletRoute} />;
  if (user && !user.daemon) return <RouteCanvasFallback wallet={walletRoute} />;

  return <>{children}</>;
}
